import { useState, useEffect, useRef } from "react";
import { Button } from "@/common/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";

import FinancialTable from "./FinancialTable";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";

interface FinancialsTabProps {
  company: any;
  setIsLoading: (loading: boolean) => void;
}

const FinancialsTab = ({ company, setIsLoading }: FinancialsTabProps) => {
  // Generate year options from 2016 to 2026
  const yearOptions = Array.from({ length: 11 }, (_, i) => 2016 + i);

  const [sectionViewTypes, setSectionViewTypes] = useState<
    Record<string, "standalone" | "consolidated">
  >({
    "Quarterly Results": "consolidated", // Default to consolidated
  });
  const [yearConfig, setYearConfig] = useState<
    Record<string, { from: string; to: string }>
  >({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [quarterlyResultsData, setQuarterlyResultsData] = useState<any[]>([]);
  const [profitAndLossData, setProfitAndLossData] = useState<any[]>([]);
  const [cashFlowData, setCashFlowData] = useState<any[]>([]);
  const [balanceSheetData, setBalanceSheetData] = useState<any[]>([]);
  const [halfYearlyResultsData, setHalfYearlyResultsData] = useState<any[]>([]);
  const lastFetchParamsRef = useRef<Record<string, string>>({});

  // Call API when component mounts, company changes, or sectionViewTypes/yearConfig changes
  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!company) {
        return;
      }

      // Use co_code or id from company object
      const companyId = company.co_code?.toString() ?? company.id?.toString();

      if (!companyId) {
        console.warn("Company ID not available for financial results API call");
        return;
      }

      const sections = [
        {
          key: "quarterly",
          title: "Quarterly Results",
          fetch: ScreenerService.getQuarterlyResultsData,
          setData: setQuarterlyResultsData,
          process: (data: any[]) =>
            data.map((row: any) => {
              const leadingSpaces =
                row.COLUMNNAME.match(/^(\s*)/)?.[0].length ?? 0;
              return {
                ...row,
                indentLevel: leadingSpaces > 0 ? 1 : 0,
                COLUMNNAME: row.COLUMNNAME.trim(),
              };
            }),
        },
        {
          key: "pl",
          title: "Profit & Loss",
          fetch: ScreenerService.getProfitAndLossResultsData,
          setData: setProfitAndLossData,
          process: (data: any[]) =>
            data.map((row: any) => {
              const leadingSpaces =
                row.COLUMNNAME.match(/^(\s*)/)?.[0].length ?? 0;
              return {
                ...row,
                indentLevel: leadingSpaces > 0 ? 1 : 0,
                COLUMNNAME: row.COLUMNNAME.trim(),
              };
            }),
        },
        {
          key: "cashFlow",
          title: "Cash Flows",
          fetch: ScreenerService.getCashFlowResultsData,
          setData: setCashFlowData,
          process: (data: any[]) =>
            data.map((row: any) => {
              const leadingSpaces =
                row.COLUMNNAME.match(/^(\s*)/)?.[0].length ?? 0;
              return {
                ...row,
                indentLevel: leadingSpaces > 0 ? 1 : 0,
                COLUMNNAME: row.COLUMNNAME.trim(),
              };
            }),
        },
        {
          key: "balanceSheet",
          title: "Balance Sheet",
          fetch: ScreenerService.getBalanceSheetResultsData,
          setData: setBalanceSheetData,
          process: (data: any[]) =>
            data.map((row: any) => {
              const leadingSpaces =
                row.COLUMNNAME.match(/^\s*/)?.[0].length ?? 0;
              return {
                ...row,
                COLUMNNAME: row.COLUMNNAME.trim(),
                indentLevel: leadingSpaces > 0 ? 1 : 0,
              };
            }),
        },
        {
          key: "halfYearlyResults",
          title: "Half Yearly Results",
          fetch: ScreenerService.getHalfYearlyResultsData,
          setData: setHalfYearlyResultsData,
          process: (data: any[]) =>
            data.map((row: any) => {
              const leadingSpaces =
                row.COLUMNNAME.match(/^\s*/)?.[0].length ?? 0;
              return {
                ...row,
                COLUMNNAME: row.COLUMNNAME.trim(),
                indentLevel: leadingSpaces > 0 ? 1 : 0,
              };
            }),
        },
      ];

      const promises: Promise<void>[] = [];
      let hasUpdates = false;

      // Identify which sections need fetching
      for (const section of sections) {
        const viewType = sectionViewTypes[section.title] ?? "consolidated";
        const fromYear = yearConfig[section.title]?.from ?? "2016";
        const toYear = yearConfig[section.title]?.to ?? "2026";

        const paramKey = `${companyId}-${section.key}-${viewType}-${fromYear}-${toYear}`;

        if (lastFetchParamsRef.current[section.key] !== paramKey) {
          hasUpdates = true;
          // Optimistically update ref to prevent double-fire in strict mode
          // (Though strict mode might fire effect twice, the second run will see updated ref if we mutate it sync-ish, but async fetch is later)
          // Actually, we should update only on success or start?
          // To be safe against strict mode, we update ref immediately for "in-flight".
          // But strict mode runs effect cleanup then effect setup.
          // Ref persists. So updating ref here is good.
          lastFetchParamsRef.current[section.key] = paramKey;

          promises.push(
            (async () => {
              try {
                const response = await section.fetch(
                  companyId,
                  viewType,
                  fromYear,
                  toYear
                );
                if (response?.success && response?.data) {
                  const processed = section.process(response.data);
                  section.setData(processed);
                } else {
                  section.setData([]);
                }
              } catch (error) {
                console.error(`Error fetching ${section.title} data:`, error);
                section.setData([]);
              }
            })()
          );
        }
      }

      if (hasUpdates) {
        setIsLoading(true);
        await Promise.all(promises);
        setIsLoading(false);
      }
    };

    fetchFinancialData();
  }, [company, sectionViewTypes, yearConfig]);

  // Toggle row expansion
  const toggleRow = (sectionTitle: string, columnName: string) => {
    const key = `${sectionTitle}-${columnName}`;
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="mx-auto space-y-12 duration-500 animate-in fade-in">
      {[
        {
          title: "Quarterly Results",
          standalone: company?.quarterlyResults,
          data: quarterlyResultsData,
          consolidated: company?.consolidatedQuarterlyResults,
        },
        {
          title: "Profit & Loss",
          standalone: company?.yearlyResults,
          data: profitAndLossData,
          consolidated: company?.consolidatedResults,
        },
        {
          title: "Balance Sheet",
          standalone: company?.balanceSheet,
          data: balanceSheetData,
          consolidated: company?.consolidatedBalanceSheet,
        },
        {
          title: "Cash Flows",
          standalone: company?.cashFlow,
          data: cashFlowData,
          consolidated: company?.consolidatedCashFlow,
        },
        {
          title: "Half Yearly Results",
          standalone: company?.halfYearlyResults,
          data: halfYearlyResultsData,
          consolidated: company?.consolidatedHalfYearlyResults,
        },
      ].map(section => {
        const currentView = sectionViewTypes[section.title] ?? "consolidated";

        return (
          <div key={section.title} className="space-y-4">
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="flex flex-col justify-between gap-4 p-6 md:flex-row md:items-center">
                <div className="space-y-1">
                  <h3 className="text-xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                  <p className="text-sm font-semibold text-gray-500">
                    {currentView === "consolidated"
                      ? `Consolidated Figures in Rs. Crores`
                      : `Standalone Figures in Rs. Crores`}
                  </p>
                </div>

                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  {/* From and To Year Dropdowns */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-600">
                      From:
                    </label>
                    <Select
                      value={yearConfig[section.title]?.from ?? "2016"}
                      onValueChange={val =>
                        setYearConfig(prev => ({
                          ...prev,
                          [section.title]: {
                            ...(prev[section.title] ?? {
                              from: "2016",
                              to: "2026",
                            }),
                            from: val,
                          },
                        }))
                      }
                    >
                      <SelectTrigger className="h-8 w-[100px] text-[12px]">
                        <SelectValue placeholder="From" />
                      </SelectTrigger>
                      <SelectContent usePortal={false}>
                        {yearOptions.map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <label className="text-xs font-medium text-gray-600">
                      To:
                    </label>
                    <Select
                      value={yearConfig[section.title]?.to ?? "2026"}
                      onValueChange={val =>
                        setYearConfig(prev => ({
                          ...prev,
                          [section.title]: {
                            ...(prev[section.title] ?? {
                              from: "2016",
                              to: "2026",
                            }),
                            to: val,
                          },
                        }))
                      }
                    >
                      <SelectTrigger className="h-8 w-[100px] text-[12px]">
                        <SelectValue placeholder="To" />
                      </SelectTrigger>
                      <SelectContent usePortal={false}>
                        {yearOptions.map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Standalone and Consolidated Buttons */}
                  <div className="flex self-start rounded-lg bg-gray-100 p-1 md:self-auto">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 rounded-md px-4 text-[12px] font-bold transition-all ${
                        currentView === "standalone"
                          ? "bg-white text-[#00D09C] shadow-sm"
                          : "text-gray-500"
                      }`}
                      onClick={() =>
                        setSectionViewTypes(prev => ({
                          ...prev,
                          [section.title]: "standalone",
                        }))
                      }
                    >
                      Standalone
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 rounded-md px-4 text-[12px] font-bold transition-all ${
                        currentView === "consolidated"
                          ? "bg-white text-[#00D09C] shadow-sm"
                          : "text-gray-500"
                      }`}
                      onClick={() =>
                        setSectionViewTypes(prev => ({
                          ...prev,
                          [section.title]: "consolidated",
                        }))
                      }
                    >
                      Consolidated
                    </Button>
                  </div>
                </div>
              </div>

              <div className="relative overflow-x-auto">
                <FinancialTable
                  data={section.data ?? []}
                  title={section.title}
                  expandedRows={expandedRows}
                  onToggleRow={toggleRow}
                  viewType={currentView}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinancialsTab;
